import { useState, useEffect, useCallback } from 'react';
import { useGetNotifications, useMarAsAllRead } from '@/services/notifications';
import { NotificationsResponse } from '@/services/notifications';
import useWebSocketConnection from './useWebSocketConnection';

const useNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationsResponse | null>(null);
  const { socket } = useWebSocketConnection();

  const { data, isLoading, error, refetch } = useGetNotifications();
  const { mutateAsync: markAsAllRead } = useMarAsAllRead();

  useEffect(() => {
    if (data) {
      setNotifications(data);
    }
  }, [data]);

  useEffect(() => {
    if (socket) {
      const handleMessage = (event: MessageEvent) => {
        const newNotification = JSON.parse(event.data) as {
          action: string;
          notifications?: NotificationsResponse;
        };

        if (newNotification.action === 'latest_notifications' && newNotification.notifications) {
          const latestNotifications = newNotification.notifications;

          setNotifications((prevNotifications) => {
            if (!prevNotifications) {
              return latestNotifications;
            }

            const currentIds = new Set<number>();

            (['Order', 'Inventory', 'Payment', 'Promotion'] as const).forEach((key) => {
              const category = prevNotifications[key];
              if (Array.isArray(category)) {
                category.forEach((n) => currentIds.add(n.id));
              }
            });

            const filteredLatest: NotificationsResponse = {
              Order: latestNotifications.Order?.filter((n) => !currentIds.has(n.id)) ?? [],
              Inventory: latestNotifications.Inventory?.filter((n) => !currentIds.has(n.id)) ?? [],
              Payment: latestNotifications.Payment?.filter((n) => !currentIds.has(n.id)) ?? [],
              Promotion: latestNotifications.Promotion?.filter((n) => !currentIds.has(n.id)) ?? [],
            };

            return {
              Order: [...(filteredLatest.Order ?? []), ...(prevNotifications.Order ?? [])],
              Inventory: [
                ...(filteredLatest.Inventory ?? []),
                ...(prevNotifications.Inventory ?? []),
              ],
              Payment: [...(filteredLatest.Payment ?? []), ...(prevNotifications.Payment ?? [])],
              Promotion: [
                ...(filteredLatest.Promotion ?? []),
                ...(prevNotifications.Promotion ?? []),
              ],
            };
          });
        }
      };

      socket.onmessage = handleMessage;

      return () => {
        socket.onmessage = null;
      };
    }
  }, [socket]);

  const markReadNotification = useCallback(
    (notificationId: number, category: keyof NotificationsResponse) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        const message = JSON.stringify({
          action: 'mark_read',
          notification_id: notificationId,
        });

        socket.send(message);

        // Optimistic update: only update the specified category for efficiency
        setNotifications((prevNotifications) => {
          if (!prevNotifications || !prevNotifications[category]) {
            return prevNotifications;
          }

          return {
            ...prevNotifications,
            [category]: prevNotifications[category].map((n) =>
              n.id === notificationId ? { ...n, is_read: true } : n,
            ),
          };
        });

        // Refetch to sync with server state
        refetch();
      } else {
        return;
      }
    },
    [socket, refetch],
  );

  return {
    notifications,
    isLoading,
    error,
    markReadNotification,
    markAsAllRead,
  };
};

export default useNotifications;
